import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ChatLogModal from './ChatModal'
import { changeDateTimeFormat } from '../shared/Helper'
import * as Styles from './style'
import axios from 'axios'
import Cookies from 'universal-cookie'
import LoadingOverlay from 'react-loading-overlay'
import { BounceLoader } from 'react-spinners'
import { getAllUsers } from '../reduxLogic/reducers/auth/getAllUsersSlice'
import { getAllChats } from '../reduxLogic/reducers/chats/getAllChatsSlice'

const cookies = new Cookies()

// V2 API Configuration
const QA_API = process.env.REACT_APP_QA_MODEL_URL || 'https://qamodeldevapi.thehumblebee.co'
const ADMIN_API = process.env.REACT_APP_API_APP_USER_URL || 'https://api.udyamita.org/user-app'
const FIELD_API = process.env.REACT_APP_API_APP_FIELD_URL || 'https://api.udyamita.org/field-view'

const USERS_TABLE_COLUMNS = [
  { Header: 'Mobile', accessor: 'mobile', width: 120 },
  { Header: 'Name', accessor: 'name', width: 150 },
  { Header: 'Total Messages', accessor: 'total_messages', width: 100 },
  { Header: 'Total Read', accessor: 'total_read', width: 80 },
  { Header: 'Total Unread', accessor: 'total_unread', width: 80 },
  { Header: 'Last Activity', accessor: 'latest_activity', width: 150 },
  { Header: 'Actions', accessor: 'actions', width: 100 },
]

const ChatsTable = () => {
  const dispatch = useDispatch()
  const currentToken = cookies.get('access_token')

  // Get allUsers from Redux for enrichment (same as previous working version)
  const { allUsers } = useSelector(state => state.allUsers)

  // State for V2 API with server-side pagination
  const [chatLogs, setChatLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 })

  // Global totals (fetched in background from V1 API - don't block on this)
  const [globalTotals, setGlobalTotals] = useState({
    total_users: 0,
    total_messages: 0,
    total_read: 0,
    total_unread: 0
  })

  // Modal state
  const [openModal, setOpenModal] = useState(false)
  const [selectedChatMessages, setSelectedChatMessages] = useState([])

  // Basic profiles cache (for users who haven't completed baseline survey)
  const [basicProfiles, setBasicProfiles] = useState([])

  // Fetch allUsers on mount for enrichment
  useEffect(() => {
    dispatch(getAllUsers())
    dispatch(getAllChats()) // Dispatch to get global totals (runs in background)
  }, [dispatch])

  // Get global totals from Redux (from V1 API via getAllChats thunk)
  const { allChats } = useSelector(state => state.allChats)

  // Update globalTotals when allChats data loads (background update, doesn't block UI)
  useEffect(() => {
    if (allChats && allChats.chat_logs) {
      setGlobalTotals({
        total_users: allChats.chat_logs?.length || 0,
        total_messages: allChats.total_messages || 0,
        total_read: allChats.total_read || allChats.total_messages || 0,
        total_unread: allChats.total_unread || 0
      })
    }
  }, [allChats])

  // Fetch basic profiles on mount (for users who haven't completed baseline survey)
  useEffect(() => {
    const fetchBasicProfiles = async () => {
      try {
        const res = await axios.get(
          `${FIELD_API}/surveys/bp/admin-base-profile`,
          {
            headers: {
              source: 'humblebee_admin',
              Authorization: `Bearer ${currentToken}`
            }
          }
        )
        setBasicProfiles(res.data?.data || [])
        console.log('Basic profiles loaded:', res.data?.data?.length || 0)
      } catch (err) {
        console.log('Error fetching basic profiles:', err.message)
      }
    }
    if (currentToken) {
      fetchBasicProfiles()
    }
  }, [currentToken])

  // Fetch user profile by ID (for App chats) - same as Surveys/index.js getUser()
  const fetchUserById = async (userId) => {
    try {
      const res = await axios.get(`${ADMIN_API}/user/user-id-details/${userId}`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
          source: 'humblebee_admin'  // Required header for this API
        }
      })
      return res.data  // Contains { user, trainee } object
    } catch (err) {
      console.log('Error fetching user:', userId, err.message)
      return null
    }
  }

  // Fetch user profile by mobile (for WhatsApp chats)
  const fetchUserByMobile = async (mobileNumber) => {
    try {
      const res = await axios.get(`${ADMIN_API}/user/getUserFromMobile?mobileNumber=${mobileNumber}`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      })
      return res.data
    } catch (err) {
      console.log('Error fetching user by mobile:', mobileNumber, err.message)
      return null
    }
  }

  // Fetch V2 chat logs with pagination
  const fetchChatLogsV2 = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch App Chat Logs V2
      const appResponse = await axios.get(`${QA_API}/app/chat-logs-v2?page=${page}&limit=${limit}`)
      const appData = appResponse.data

      if (!appData.success) {
        throw new Error('Failed to fetch chat logs')
      }

      // Enrich user data using 3-tier fallback:
      // 1. allUsers from Redux (admin users)
      // 2. user-id-details API (advance profile users)
      // 3. basicProfiles (basic profile users who haven't completed survey)
      const enrichedLogs = await Promise.all(
        appData.chat_logs.map(async (log) => {
          let userName = '-'
          let userMobile = log.uid || '-'

          // 1. First try to find in allUsers (for admin users)
          let matchedUser = allUsers?.find(
            user => user.userInfo?._id === log.uid ||
              user.userInfo?.mobileNumber === log.uid ||
              user._id === log.uid
          )

          // 2. If not found in allUsers, fetch from user-id-details API (advance profiles)
          if (!matchedUser && log.uid && currentToken) {
            const userDetails = await fetchUserById(log.uid)
            if (userDetails && userDetails.user) {
              matchedUser = { userInfo: userDetails.user }
            }
          }

          // 3. If still not found, check basicProfiles (basic profile users)
          if (!matchedUser && log.uid && basicProfiles.length > 0) {
            const basicUser = basicProfiles.find(
              bp => bp._id === log.uid || bp.id === log.uid
            )
            if (basicUser) {
              matchedUser = {
                userInfo: {
                  name: basicUser.full_name,
                  mobileNumber: basicUser.mobile_number
                }
              }
            }
          }

          // Extract name and mobile from matched user
          if (matchedUser) {
            userName = matchedUser.userInfo?.name || matchedUser.name || '-'
            userMobile = matchedUser.userInfo?.mobileNumber || matchedUser.mobileNumber || log.uid || '-'
          }

          return {
            ...log,
            name: userName,
            mobile: userMobile,
            // For App chats: total_read = total_messages (all messages are read)
            total_read: log.total_read || log.total_messages || 0,
            total_unread: log.total_unread || 0,
            latest_activity: log.latest_activity ? changeDateTimeFormat(log.latest_activity) : '-'
          }
        })
      )

      setChatLogs(enrichedLogs)
      setPagination(appData.pagination)

    } catch (err) {
      console.error('Error fetching V2 chat logs:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [page, limit, allUsers, currentToken, basicProfiles])

  useEffect(() => {
    fetchChatLogsV2()
  }, [fetchChatLogsV2])

  const handleModalToggle = (log) => {
    setOpenModal(prev => !prev)
    if (log?.messages) {
      setSelectedChatMessages(log.messages.slice().reverse())
    }
  }

  const handleNextPage = () => {
    if (page < pagination.totalPages) {
      setPage(prev => prev + 1)
    }
  }

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(prev => prev - 1)
    }
  }

  const handleGotoPage = (pageNum) => {
    if (pageNum >= 1 && pageNum <= pagination.totalPages) {
      setPage(pageNum)
    }
  }

  return (
    <LoadingOverlay
      active={loading}
      spinner={<BounceLoader color="#ef5a20" />}
      text="Loading chat logs..."
    >
      <Styles.UserBox>
        <div style={{ height: '10vh' }}></div>
        <Styles.TableContainer>
          <Styles.SearchContainer>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '10px',
              }}
            >
              <div>
                <p style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                  Total Users Count:
                </p>
                <p>{globalTotals.total_users || pagination.total}</p>
              </div>
              <div>
                <p style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                  Total Messages Sent:
                </p>
                <p>{globalTotals.total_messages}</p>
              </div>
              <div>
                <p style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                  Total Messages Read:
                </p>
                <p>{globalTotals.total_read}</p>
              </div>
              <div>
                <p style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                  Total Messages Unread:
                </p>
                <p>{globalTotals.total_unread}</p>
              </div>
            </div>

            {openModal && (
              <ChatLogModal
                isOpen={openModal}
                onClose={() => setOpenModal(false)}
                chatLogs={selectedChatMessages}
              />
            )}
          </Styles.SearchContainer>

          {error ? (
            <h3 style={{ color: 'red' }}>Error: {error}</h3>
          ) : (
            <Styles.Table>
              <Styles.THead>
                <Styles.THeadRow>
                  {USERS_TABLE_COLUMNS.map((col) => (
                    <Styles.TH key={col.Header} style={{ width: col.width }}>
                      {col.Header}
                    </Styles.TH>
                  ))}
                </Styles.THeadRow>
              </Styles.THead>

              <Styles.TBody>
                {chatLogs.map((log, idx) => (
                  <Styles.TBodyRow key={log.uid || idx}>
                    <Styles.TD>{log.mobile}</Styles.TD>
                    <Styles.TD>{log.name}</Styles.TD>
                    <Styles.TD>{log.total_messages}</Styles.TD>
                    <Styles.TD>{log.total_read}</Styles.TD>
                    <Styles.TD>{log.total_unread}</Styles.TD>
                    <Styles.TD>{log.latest_activity}</Styles.TD>
                    <Styles.TD>
                      <Styles.ActionSpan>
                        <button
                          style={{
                            backgroundColor: 'green',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '5px 10px',
                            borderRadius: '4px',
                          }}
                          onClick={() => handleModalToggle(log)}
                        >
                          View Chat Log
                        </button>
                      </Styles.ActionSpan>
                    </Styles.TD>
                  </Styles.TBodyRow>
                ))}
              </Styles.TBody>
            </Styles.Table>
          )}
        </Styles.TableContainer>

        <Styles.PaginationmainContainer>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <span>
              <strong>
                Page {page} of {pagination.totalPages} ({pagination.total} total users)
              </strong>
            </span>
            <select
              value={limit}
              onChange={e => {
                setLimit(Number(e.target.value))
                setPage(1)
              }}
              style={{
                padding: '5px 10px',
                borderRadius: '4px',
                border: '1px solid #d9d9d9',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              {[100, 500, 1000, 2000, 3000, 5000].map(pageSizeOption => (
                <option key={pageSizeOption} value={pageSizeOption}>
                  Show {pageSizeOption}
                </option>
              ))}
            </select>
          </div>
          <Styles.PaginationDiv>
            <Styles.PaginationButton
              onClick={() => handleGotoPage(1)}
              disabled={page === 1}
            >
              {'<<'}
            </Styles.PaginationButton>
            <Styles.PaginationButton
              onClick={handlePrevPage}
              disabled={page === 1}
            >
              Previous
            </Styles.PaginationButton>

            <Styles.PaginationInputDiv>
              <input
                type="number"
                value={page}
                min={1}
                max={pagination.totalPages}
                onChange={e => {
                  const val = parseInt(e.target.value) || 1
                  handleGotoPage(val)
                }}
              />
            </Styles.PaginationInputDiv>

            <Styles.PaginationButton
              onClick={handleNextPage}
              disabled={page >= pagination.totalPages}
            >
              Next
            </Styles.PaginationButton>
            <Styles.PaginationButton
              onClick={() => handleGotoPage(pagination.totalPages)}
              disabled={page >= pagination.totalPages}
            >
              {'>>'}
            </Styles.PaginationButton>
          </Styles.PaginationDiv>
        </Styles.PaginationmainContainer>
      </Styles.UserBox>
    </LoadingOverlay>
  )
}

export default ChatsTable
